export { getMonthNumber, useParsedDates, formatChartDate } from './dateUtils';

export const CHART_METRIC_COLORS = [
  '#e41a1c', // Bright Red
  '#377eb8', // Royal Blue
  '#4daf4a', // Grass Green
  '#984ea3', // Purple
  '#ff7f00', // Orange
  '#b15928', // Rust Brown
  '#7a4900', // Dark Brown
  '#f781bf', // Pink
  '#555555', // Dark Gray
  '#00ced1', // Turquoise
  '#8dd3c7', // Mint
  '#bc80bd', // Lavender
  '#d95f02', // Burnt Orange
  '#006400'  // Dark Green
];

import type { ILabTest } from '../../../../global/entities/LabTest/LabTest';
import type { IBiomarker } from '../../../../global/entities/Biomarker/Biomarker';
import { QuestionnaireScoringMode } from '../../../../global/types';

interface TransformedChartDataPoint {
  FormattedDate: string;
  ShortDate: string;
  [key: string]: string | number | null;
}

/**
 * Transforms raw questionnaire score data into a chart-friendly format.
 * Two tasks for the same questionnaire+category due on the same calendar day
 * (e.g. an ad-hoc duplicate) are kept as separate points, disambiguated by
 * taskID, instead of one silently overwriting the other. Entries for
 * different questionnaires/categories on the same day still share one row.
 */
export function transformQuestionnaireScoreData(
  questionnaireScoreData: any[] = [],
  labTests: ILabTest[] = [],
  startTime: string,
  endTime: string,
  totalGrouping: boolean,
  selectedPkMetrics: string[] = [],
  selectedExternalMetrics: string[] = [],
  pkBiomarkerIds: string[] = []
) {
  const dataByDate: Record<string, TransformedChartDataPoint> = {};
  const { addValue, formatDateKey } = getChartDataHelpers(dataByDate);

  const buildMetricKey = (dataPoint: any) => {
    const questionnaireId = dataPoint.questionnaireID?.toString();
    const shortName = dataPoint.shortName || '';
    const category = dataPoint.category || 'Total';
    return `{${questionnaireId}-${shortName}}-${category}`;
  };

  // Count entries sharing the same day + metric, so only genuine collisions
  // (e.g. two tasks due the same day for the same questionnaire+category)
  // get disambiguated by taskID. Entries for a different metric on the same
  // day still share one chart row.
  const dayMetricCounts: Record<string, number> = {};
  questionnaireScoreData.forEach(dataPoint => {
    const dayKey = new Date(dataPoint.dateCompleted).toISOString().split('T')[0];
    const collisionKey = `${dayKey}|${buildMetricKey(dataPoint)}`;
    dayMetricCounts[collisionKey] = (dayMetricCounts[collisionKey] || 0) + 1;
  });

  const hasTotal: Record<string, Set<string>> = {};

  questionnaireScoreData.forEach((dataPoint, index) => {
    const dateObj = new Date(dataPoint.dateCompleted);
    const dayKey = dateObj.toISOString().split('T')[0];
    const questionnaireId = dataPoint.questionnaireID?.toString();
    const category = dataPoint.category || 'Total';
    const metricKey = buildMetricKey(dataPoint);
    const collisionKey = `${dayKey}|${metricKey}`;

    const dateKey = dayMetricCounts[collisionKey] > 1
      ? `${dayKey}__${dataPoint.taskID ?? index}`
      : dayKey;
    const shortDate = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

    if (!dataByDate[dateKey]) {
      dataByDate[dateKey] = {
        FormattedDate: dayKey,
        ShortDate: shortDate
      };
    }

    // Mark if this date/questionnaire has a total
    if (category === 'Total') {
      if (!hasTotal[dateKey]) hasTotal[dateKey] = new Set();
      hasTotal[dateKey].add(questionnaireId);
    }

    // Calculate score based on scoring mode: Average or Sum
    const calculatedScore = dataPoint.scoringModeID === QuestionnaireScoringMode.Sum
      ? dataPoint.score
      : dataPoint.score / dataPoint.scoreCount;

    dataByDate[dateKey][metricKey] = calculatedScore;
  });

  // If totalGrouping is true, remove non-total keys from the data point for the graph (but keep for hover)
  if (totalGrouping) {
    Object.entries(dataByDate).forEach(([dateKey, dataPoint]) => {
      if (hasTotal[dateKey]) {
        hasTotal[dateKey].forEach(qId => {
          Object.keys(dataPoint).forEach(key => {
            const keyMatch = key.match(/^\{(.+?)\}-(.+)$/);
            if (
              keyMatch &&
              !key.endsWith('-Total') &&
              key.startsWith(`{${qId}-`)
            ) {
              let hoverData = (dataPoint as any).hoverData;
              if (!hoverData) {
                hoverData = {};
                Object.defineProperty(dataPoint, 'hoverData', {
                  value: hoverData,
                  enumerable: true,
                  writable: true,
                });
              }
              hoverData[key] = dataPoint[key];
              delete dataPoint[key];
            }
          });
        });
      }
    });
  }

  // --- Process lab test data ---
  const selectedLabMetrics = [
    ...selectedPkMetrics,
    ...selectedExternalMetrics
  ];

  processLabTestData({
    labTests,
    startTime,
    endTime,
    selectedLabMetrics,
    pkBiomarkerIds,
    addValue,
    formatDateKey,
  });

  // Return sorted array of data points
  return Object.values(dataByDate).sort(
    (a, b) => new Date(a.FormattedDate).getTime() - new Date(b.FormattedDate).getTime()
  );
}

/**
 * Returns helpers for chart data transformation: addValue and formatDateKey.
 * @param dataByDate The object to store date-keyed data
 * @returns { addValue, formatDateKey, formatShortDate }
 */
export function getChartDataHelpers(dataByDate: Record<string, any>) {
  const formatDateKey = (date: Date) => date.toISOString().split('T')[0];
  const formatShortDate = (date: Date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  function addValue(dateKey: string, dateObj: Date, key: string, value: number | string | null) {
    if (!dataByDate[dateKey]) {
      dataByDate[dateKey] = {
        FormattedDate: dateKey,
        ShortDate: formatShortDate(dateObj)
      } as unknown as Record<string, any>;
    }
    dataByDate[dateKey][key] = value;
  }
  return { addValue, formatDateKey, formatShortDate };
}

/**
 * Usage pattern:
 *   const dataByDate = {};
 *   const { addValue, formatDateKey } = getChartDataHelpers(dataByDate);
 *   processLabTestData({ labTests, startTime, endTime, selectedLabMetric, METHYLPHENIDATE_LAB_ID, addValue, formatDateKey });
 */

/**
 * Processes lab test data, handling PK biomarker averaging and selected biomarker filtering.
 * @param labTests Array of lab test objects
 * @param startTime Start time string (e.g., '09:00')
 * @param endTime End time string (e.g., '14:00')
 * @param selectedLabMetrics Array of selected lab metric ids (includes PK, non-PK, and external biomarkers)
 * @param pkBiomarkerIds Array of PK biomarker IDs in 'lab-X' format for time averaging
 * @param addValue Callback to add a value to the data structure (dateKey, dateObj, key, value)
 * @param formatDateKey Function to format a date as a key (YYYY-MM-DD)
 */
export function processLabTestData({
  labTests,
  startTime,
  endTime,
  selectedLabMetrics = [],
  pkBiomarkerIds = [],
  addValue,
  formatDateKey,
}: {
  labTests: ILabTest[];
  startTime: string;
  endTime: string;
  selectedLabMetrics?: string[];
  pkBiomarkerIds?: string[];
  addValue: (dateKey: string, dateObj: Date, key: string, value: number | string | null) => void;
  formatDateKey: (date: Date) => string;
}) {
  function isWithinTimeRange(time: string, start: string, end: string) {
    return time >= start + ':00' && time <= end + ':00';
  }

  if (labTests && labTests.length > 0 && selectedLabMetrics.length > 0) {
    // Handle PK biomarkers with time averaging
    const pkMetricsWithTimeRange = selectedLabMetrics.filter(metric => 
      startTime && endTime && pkBiomarkerIds.includes(metric)
    );
    
    // Process PK biomarkers with time averaging
    // Special logic: If only one test exists for a day, show it regardless of time interval
    pkMetricsWithTimeRange.forEach(selectedLabMetric => {
      const labTestsByDate: Record<string, ILabTest[]> = {};
      const allLabTestsByDate: Record<string, ILabTest[]> = {}; // Track all tests per date
      
      for (const labTest of labTests) {
        if (`lab-${labTest.biomarkerId}` === selectedLabMetric) {
          const dateObj = new Date(labTest.dateMeasured);
          const dateKey = formatDateKey(dateObj);
          const timeKey = dateObj.toISOString().split('T')[1]?.split('.')[0];
          
          // Track all tests for this date
          if (!allLabTestsByDate[dateKey]) allLabTestsByDate[dateKey] = [];
          allLabTestsByDate[dateKey].push(labTest);
          
          // Track tests within time range
          if (timeKey && isWithinTimeRange(timeKey, startTime, endTime)) {
            if (!labTestsByDate[dateKey]) labTestsByDate[dateKey] = [];
            labTestsByDate[dateKey].push(labTest);
          }
        }
      }
      
      // Process each date
      for (const [dateKey, allTestsForDate] of Object.entries(allLabTestsByDate)) {
        const dateObj = new Date(dateKey);
        const key = `lab-${allTestsForDate[0].biomarkerId}`;
        
        // Check if we have tests within time range for this date
        const testsInRange = labTestsByDate[dateKey];
        
        if (testsInRange && testsInRange.length > 0) {
          // Use tests within time range (normal behavior)
          const avg = testsInRange.reduce((acc, t) => acc + t.value, 0) / testsInRange.length;
          addValue(dateKey, dateObj, key, avg);
        } else if (allTestsForDate.length === 1) {
          // If only one test for this date and no tests in range, use the single test
          addValue(dateKey, dateObj, key, allTestsForDate[0].value);
        }
        // If multiple tests exist but none in range, don't show any data (skip this date)
      }
    });

    // Process all other selected biomarkers normally (non-PK and external biomarkers)
    const otherSelectedMetrics = selectedLabMetrics.filter(metric => 
      !pkMetricsWithTimeRange.includes(metric)
    );
    
    for (const labTest of labTests) {
      const key = `lab-${labTest.biomarkerId}`;
      // Only add lab test data for selected biomarkers
      if (otherSelectedMetrics.includes(key)) {
        const dateObj = new Date(labTest.dateMeasured);
        const dateKey = formatDateKey(dateObj);
        addValue(dateKey, dateObj, key, labTest.value);
      }
    }
  }
}

/**
 * Groups lab tests by specimen type for display as metrics, including units in names
 */
export function groupLabTests(labTests: ILabTest[] = []) {
  const uniqueMetrics = new Map();
  labTests.forEach((labtest, index) => {
    const metricId = `lab-${labtest.biomarkerId}`;
    if (!uniqueMetrics.has(metricId)) {
      // Include unit in the name if it exists and is not empty
      const nameWithUnit = labtest.unit && labtest.unit.trim() !== '' 
        ? `${labtest.description} (${labtest.unit})`
        : labtest.description;
        
      uniqueMetrics.set(metricId, {
        id: metricId,
        name: nameWithUnit,
        display: nameWithUnit,
        color: CHART_METRIC_COLORS[(index + 10) % CHART_METRIC_COLORS.length],
        descriptions: labtest.description,
        isLabTest: true
      });
    }
  });
  return Array.from(uniqueMetrics.values());
}

/**
 * Groups physiological types for display as metrics, including units in names
 * @param physiologicalTypes Array of physiological type objects
 * @returns Array of metric objects with units included in names
 */
export function groupPhysiologicalMetrics(physiologicalTypes: any[] = []) {
  const grouped: Record<string, any[]> = {};
  physiologicalTypes.forEach(type => {
    const baseName = type.name.split(' (')[0];
    if (!(type.subtypes && type.subtypes.split(',').length > 1)) {
      if (!grouped[baseName]) grouped[baseName] = [];
      grouped[baseName].push({
        id: type.id,
        name: type.name,
        unitPartsUnits: [],
        unitName: type.unitName,
        abbreviation: type.abbreviation,
        onlyShowInTypeSelection: type.onlyShowInTypeSelection,
        unitID: type.unitID
      });
    }
  });

  return Object.entries(grouped).map(([baseName, types], index) => ({
    id: types[0].id.toString() + '-' + types[0].abbreviation,
    name: types[0].unitName ? `${baseName} (${types[0].unitName})` : baseName,
    display: types[0].unitName ? `${types[0].abbreviation} (${types[0].unitName})` : types[0].abbreviation,
    color: CHART_METRIC_COLORS[index % CHART_METRIC_COLORS.length],
    types: types,
  }));
}

/**
 * Groups biomarkers by type, separating PK and non-PK biomarkers
 * @param biomarkers Array of biomarker objects
 * @returns Object with separate arrays for PK and non-PK biomarkers, both sorted alphabetically
 */
export function groupBiomarkersByType(biomarkers: IBiomarker[] = []): {
  pkBiomarkers: IBiomarker[];
  nonPkBiomarkers: IBiomarker[];
} {
  if (!biomarkers || biomarkers.length === 0) {
    return {
      pkBiomarkers: [],
      nonPkBiomarkers: []
    };
  }

  // Separate biomarkers by type
  const pkBiomarkers = biomarkers.filter(biomarker => biomarker.isPK);
  const nonPkBiomarkers = biomarkers.filter(biomarker => !biomarker.isPK);

  // Sort each group alphabetically by name for consistent ordering
  const sortedPK = pkBiomarkers.sort((a, b) => a.name.localeCompare(b.name));
  const sortedNonPK = nonPkBiomarkers.sort((a, b) => a.name.localeCompare(b.name));

  // Return separate arrays
  return {
    pkBiomarkers: sortedPK,
    nonPkBiomarkers: sortedNonPK
  };
} 